import { useState } from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { logger } from '@/utils/logger';

import { useDeleteWizardMutation, useGetWizardsQuery } from '../api/wizardsApi';
import { WizardCard } from '../components/WizardCard';

/**
 * Wizard List Page - Browse and manage wizards
 * Based on dataProtect wizardList.js
 */
export const WizardListPage = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [wizardToDelete, setWizardToDelete] = useState<string | null>(null);

  const { data: wizards = [], isLoading, error } = useGetWizardsQuery(appId!);
  const [deleteWizard, { isLoading: isDeleting }] = useDeleteWizardMutation();

  const handleNewWizard = () => {
    navigate(`/a/${appId}/wiz/new`);
  };

  const handleDeleteClick = (wizardId: string) => {
    setWizardToDelete(wizardId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!wizardToDelete) return;

    try {
      await deleteWizard({ appId: appId!, wizardId: wizardToDelete }).unwrap();
      setDeleteDialogOpen(false);
      setWizardToDelete(null);
    } catch (err) {
      logger.error('Failed to delete wizard:', err);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">Failed to load wizards</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title="Wizards"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewWizard}>
            New Wizard
          </Button>
        }
      />

      {wizards.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            backgroundColor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No wizards yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first wizard to get started with guided recipe creation
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewWizard}>
            Create First Wizard
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {wizards.map((wizard) => (
            <Grid item xs={12} sm={6} md={4} key={wizard.id}>
              <WizardCard wizard={wizard} onDelete={handleDeleteClick} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setDeleteDialogOpen(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Wizard</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this wizard?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
